import { Link } from "wouter";
import { useState, useEffect, useRef } from "react";
import { applySeo } from "@/lib/seo";
import { CheckoutButton } from "@/components/CheckoutButton";
import { AnalysisPreviewCard, PREVIEW_DATA } from "@/components/AnalysisPreviewCard";
import { BAROMETER_PRICES, PAKET_PRICES, MARKETING_REALITAETSCHECK_MIT_VIDEOCALL, VIDEOCALL_1H_AUFPREIS, formatPrice } from "@shared/prices";

const LOGO_URL = "/manus-storage/logo_2d439bd1.jpg";
const PORTRAIT_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663476281654/fDRgangGWFphFKzWtNAEav/larissa-portrait_abbef783.jpg";

const C = {
  accent: "#6d0000",       // Bordeaux – Premium, Autorität
  accentDark: "#4f0f0f",
  accentLight: "#6d000012",
  teal: "#115751",
  bg: "#ffffff",
  bgGray: "#f2f2f2",
  text: "#000000",
  muted: "#6b7280",
  border: "#e5e7eb",
};

function AnimatedScore({ target, duration = 1800 }: { target: number; duration?: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return <span ref={ref}>{count}</span>;
}

function DemoBar({ label, score, max, color, delay }: { label: string; score: number; max: number; color: string; delay: number }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setWidth((score / max) * 100), delay);
    return () => clearTimeout(t);
  }, [score, max, delay]);
  return (
    <div className="mb-2">
      <div className="flex justify-between text-xs mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <span className="text-gray-700">{label}</span>
        <span className="font-bold" style={{ color }}>{score}/{max}</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${width}%`, backgroundColor: color, transitionDuration: "1.2s", transitionTimingFunction: "ease-out" }} />
      </div>
    </div>
  );
}

const ALL_BAROMETER = [
  { id: "sensorik", icon: "/manus-storage/11_094992a3.svg", name: "Sensorik-Barometer™", price: BAROMETER_PRICES.sensorik, color: "#6d0000", path: "/sensorik", category: "marketing", desc: "Emotionale Wirkung, Farben, Typografie, Bildsprache, multisensorisches Erlebnis", grade: "B+", score: 74, dimensions: [{ label: "Visuelle Hierarchie", score: 18, max: 25 }, { label: "Farbpsychologie", score: 14, max: 20 }, { label: "Typografie-Wirkung", score: 15, max: 20 }, { label: "Bildsprache", score: 16, max: 25 }, { label: "Emotionale Trigger", score: 11, max: 10 }] },
  { id: "lokal", icon: "/manus-storage/1_55991aa3.svg", name: "Lokal-Barometer™", price: BAROMETER_PRICES.lokal, color: "#115751", path: "/lokal-barometer", category: "website", desc: "Google Business Profile, lokale Sichtbarkeit, NAP-Konsistenz, Bewertungen", grade: "C", score: 51, dimensions: [{ label: "Google Business Profile", score: 15, max: 20 }, { label: "NAP-Konsistenz", score: 9, max: 15 }, { label: "Lokale Keywords", score: 12, max: 20 }, { label: "Bewertungen", score: 8, max: 15 }, { label: "Lokale Backlinks", score: 7, max: 10 }] },
  { id: "geo", icon: "/manus-storage/7_8bcad2d2.svg", name: "GEO-Barometer™", price: BAROMETER_PRICES.geo, color: "#1a3a5c", path: "/geo-barometer", category: "marketing", desc: "KI-Sichtbarkeit, strukturierte Daten, E-E-A-T, ChatGPT/Perplexity-Optimierung", grade: "C+", score: 58, dimensions: [{ label: "Strukturierte Daten", score: 12, max: 20 }, { label: "E-E-A-T Signale", score: 11, max: 20 }, { label: "KI-Zitierbarkeit", score: 10, max: 20 }, { label: "Autorität & Vertrauen", score: 14, max: 20 }, { label: "Content-Tiefe", score: 11, max: 20 }] },
  { id: "seo", icon: "/manus-storage/23_1ab7147e.svg", name: "SEO-Barometer™", price: BAROMETER_PRICES.seo, color: "#0a5c36", path: "/seo-barometer", category: "website", desc: "SEO-Grundlagen-Check: On-Page SEO, technische SEO, Core Web Vitals (Google PageSpeed), E-E-A-T – kein Backlink-Check", grade: "B", score: 67, dimensions: [{ label: "On-Page Grundlagen", score: 18, max: 25 }, { label: "Technische SEO", score: 14, max: 20 }, { label: "Ladegeschwindigkeit", score: 12, max: 20 }, { label: "Inhaltsqualität", score: 16, max: 25 }, { label: "E-E-A-T Signale", score: 7, max: 10 }] },
  { id: "conversion", icon: "/manus-storage/33_d51b7819.svg", name: "Conversion-Barometer™", price: BAROMETER_PRICES.conversion, color: "#b45309", path: "/conversion-barometer", category: "marketing", desc: "CTAs, Vertrauen, Reibungsfreiheit, Preispsychologie, Cialdini-Prinzipien", grade: "B-", score: 63, dimensions: [{ label: "Persuasion & Psychologie", score: 14, max: 20 }, { label: "Vertrauen & Trust", score: 13, max: 20 }, { label: "CTA & Value Prop.", score: 12, max: 20 }, { label: "Reibungsfreiheit", score: 14, max: 25 }, { label: "Mobile Conversion", score: 10, max: 15 }] },
  { id: "accessibility", icon: "/manus-storage/16_9c3f7718.svg", name: "Accessibility-Barometer™", price: BAROMETER_PRICES.accessibility, color: "#4338ca", path: "/accessibility-barometer", category: "website", desc: "WCAG 2.2-Konformität, ARIA-Rollen, Tastaturnavigation, European Accessibility Act", grade: "B", score: 66, dimensions: [{ label: "Wahrnehmbarkeit", score: 17, max: 25 }, { label: "Bedienbarkeit", score: 14, max: 20 }, { label: "Verständlichkeit", score: 13, max: 20 }, { label: "Robustheit", score: 8, max: 15 }, { label: "EAA-Compliance", score: 14, max: 20 }] },
  { id: "datenschutz", icon: "/manus-storage/29_7f3bb394.svg", name: "Datenschutz-Barometer™", price: BAROMETER_PRICES.datenschutz, color: "#0e7490", path: "/datenschutz-barometer", category: "website", desc: "DSGVO-Konformität, Cookie-Consent, Impressum, SSL, externe Dienste", grade: "B", score: 65, dimensions: [{ label: "Cookie-Consent", score: 14, max: 20 }, { label: "Impressum & Pflichtangaben", score: 12, max: 20 }, { label: "Datenschutzerklärung", score: 11, max: 20 }, { label: "SSL & Sicherheit", score: 16, max: 20 }, { label: "Externe Dienste", score: 12, max: 20 }] },
];

const SINGLE_TOTAL = ALL_BAROMETER.reduce((s, b) => s + b.price, 0);
const BUNDLE_PRICE = PAKET_PRICES.marketing;
const SAVINGS = SINGLE_TOTAL - BUNDLE_PRICE;

export default function MarketingRealitaetscheck() {
  useEffect(() => applySeo({
    title: "Marketing Realitäts-Check™ – Wo verlierst du Kunden? | WERBEAGENTUR Larissa Girenko",
    description: "Dein Marketing auf dem Prüfstand: SEO, Social Media, Google Ads, E-Mail-Marketing & mehr. Marketing Realitäts-Check™ deckt auf, was wirklich fehlt.",
    path: "/marketing-realitaetscheck",
  }), []);
  const [activeDemo, setActiveDemo] = useState(0);
  const [demoKey, setDemoKey] = useState(0);

  function switchDemo(idx: number) {
    setActiveDemo(idx);
    setDemoKey(k => k + 1);
  }

  const demo = ALL_BAROMETER[activeDemo];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Navigation */}
      <header className="sticky top-0 z-50 bg-white border-b" style={{ borderColor: C.border }}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link href="/">
            <div className="flex items-center gap-3 cursor-pointer">
              <img src={LOGO_URL} alt="Werbeagentur Larissa Girenko" className="w-10 h-10 object-contain" />
              <div>
                <div className="font-black uppercase text-sm tracking-widest" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.text }}>
                  Werbeagentur Larissa Girenko
                </div>
                <div className="text-xs" style={{ color: C.muted }}>Marketing Barometer Suite</div>
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link href="/">
              <button className="text-xs font-bold tracking-widest uppercase px-3 py-1.5 border" style={{ borderColor: C.border, color: C.muted, fontFamily: "'DM Sans', sans-serif" }}>
                ← Übersicht
              </button>
            </Link>
            <Link href="/website-realitaetscheck">
              <button className="text-xs font-bold tracking-widest uppercase px-3 py-1.5 border" style={{ borderColor: C.teal, color: C.teal, fontFamily: "'DM Sans', sans-serif" }}>
                Website Check
              </button>
            </Link>
          </div>
        </div>
      </header>

      {/* CI-Akzentlinie */}
      <div className="h-1 w-full" style={{ backgroundColor: C.accent }} />

      {/* Hero */}
      <section className="bg-black text-white py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ background: `radial-gradient(ellipse at 30% 50%, ${C.accent} 0%, transparent 60%)` }} />
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-10">
            <div className="md:w-[55%]">
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 text-xs font-bold tracking-widest uppercase border" style={{ borderColor: C.accent, color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>
                ● Premium-Paket · Alle 7 Barometer
              </div>
              <h1 className="font-black uppercase leading-none mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2.8rem, 6vw, 5rem)" }}>
                Marketing<br />
                <span style={{ color: C.accent }}>Realitäts-Check™</span>
              </h1>
              <p className="text-gray-300 text-base leading-relaxed mb-6 max-w-lg" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Das vollständige Bild deines Marketings. Ich analysiere nicht nur deine Website – ich analysiere, wie deine Marke wirklich wirkt: emotional, technisch, rechtlich, lokal und in der KI-Welt. 7 Barometer. Ein Realitäts-Check.
              </p>

              {/* Unterschied zum Website-Check */}
              <div className="border border-gray-700 p-4 mb-6" style={{ background: "rgba(109,0,0,0.1)" }}>
                <div className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.accent }}>Was dieser Check mehr bietet</div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { icon: "/manus-storage/11_094992a3.svg", text: "Sensorik & Emotionen" },
                    { icon: "/manus-storage/7_8bcad2d2.svg", text: "KI-Sichtbarkeit" },
                    { icon: "/manus-storage/33_d51b7819.svg", text: "Conversion-Psychologie" },
                    { icon: "+4", text: "Website-Checks inkl." },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-gray-300">
                      <img src={item.icon} alt="" className="w-5 h-5 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-end gap-4">
                <div>
                  <div className="font-black text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(3rem, 6vw, 4.5rem)", lineHeight: 1 }}>
                    {BUNDLE_PRICE} €
                  </div>
                  <div className="flex flex-wrap gap-2 mt-1 mb-2">
                    <span className="text-xs text-gray-500 line-through">{SINGLE_TOTAL} €</span>
                    <span className="text-xs font-bold px-2 py-0.5" style={{ backgroundColor: "#16a34a", color: "white" }}>−{SAVINGS} € gespart</span>
                  </div>
                  <div className="text-xs font-bold mb-2" style={{ color: C.accent }}>= {(BUNDLE_PRICE / 7).toFixed(0)} € pro Barometer</div>
                  <p className="text-xs text-yellow-500 font-bold mb-3">⏳ Aktionspreis – begrenzte Zeit</p>
                </div>
                <CheckoutButton
                  productId="marketing_realitaetscheck"
                  label="Jetzt starten"
                  accentColor={C.accent}
                  returnPath="/marketing-realitaetscheck"
                />
              </div>
            </div>

            {/* Alle Barometer-Karte */}
            <div className="md:w-[40%] border border-gray-700 p-6" style={{ background: "rgba(255,255,255,0.04)" }}>
              <div className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: C.accent }}>Alle 7 Analysen</div>
              {ALL_BAROMETER.map(b => (
                <div key={b.id} className="flex items-center justify-between py-2.5 border-b border-gray-800">
                  <div className="flex items-center gap-2">
                    <img src={b.icon} alt="" className="w-5 h-5 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                    <span className="text-sm text-white font-medium">{b.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 line-through">{b.price} €</span>
                    <span className="text-xs font-bold" style={{ color: C.accent }}>✓</span>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-4">
                <span className="text-sm font-bold text-white">Gesamt</span>
                <div className="text-right">
                  <div className="text-xs text-gray-500 line-through">{SINGLE_TOTAL} €</div>
                  <div className="font-black text-white text-xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{BUNDLE_PRICE} €</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vergleich: Website vs. Marketing Check */}
      <section className="py-16 px-4" style={{ backgroundColor: C.bgGray }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>Vergleich</div>
            <h2 className="font-black uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)" }}>
              Welcher Check ist der richtige?
            </h2>
          </div>
          {/* Unterschied-Erklärung */}
          <div className="grid md:grid-cols-3 gap-0 border border-gray-200 mb-10">
            <div className="p-6 border-b md:border-b-0 md:border-r border-gray-200">
              <p className="text-xs font-bold tracking-widest uppercase mb-2 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Zielgruppe</p>
              <div className="space-y-3">
                <div className="border-l-2 pl-3" style={{ borderColor: C.teal }}>
                  <p className="text-xs font-bold" style={{ color: C.teal }}>Website Check</p>
                  <p className="text-xs text-gray-600">Lokale Unternehmen, KMUs, die rechtlich und technisch sauber aufgestellt sein wollen</p>
                </div>
                <div className="border-l-2 pl-3" style={{ borderColor: C.accent }}>
                  <p className="text-xs font-bold" style={{ color: C.accent }}>Marketing Check</p>
                  <p className="text-xs text-gray-600">Wachstumsorientierte Unternehmen, die Emotion, Conversion und Sichtbarkeit maximieren wollen</p>
                </div>
              </div>
            </div>
            <div className="p-6 border-b md:border-b-0 md:border-r border-gray-200">
              <p className="text-xs font-bold tracking-widest uppercase mb-2 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Schwerpunkt</p>
              <div className="space-y-3">
                <div className="border-l-2 pl-3" style={{ borderColor: C.teal }}>
                  <p className="text-xs font-bold" style={{ color: C.teal }}>Website Check</p>
                  <p className="text-xs text-gray-600">Technisch, rechtlich, lokal – SEO, Datenschutz, Barrierefreiheit, lokale Sichtbarkeit</p>
                </div>
                <div className="border-l-2 pl-3" style={{ borderColor: C.accent }}>
                  <p className="text-xs font-bold" style={{ color: C.accent }}>Marketing Check</p>
                  <p className="text-xs text-gray-600">Emotional, strategisch, ganzheitlich – alle 7 Dimensionen inkl. Sensorik, Conversion & KI-Sichtbarkeit</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <p className="text-xs font-bold tracking-widest uppercase mb-2 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Ergebnis</p>
              <div className="space-y-3">
                <div className="border-l-2 pl-3" style={{ borderColor: C.teal }}>
                  <p className="text-xs font-bold" style={{ color: C.teal }}>Website Check</p>
                  <p className="text-xs text-gray-600">4 Analysen · {formatPrice(PAKET_PRICES.website)} · Compliance + Sichtbarkeit gesichert</p>
                </div>
                <div className="border-l-2 pl-3" style={{ borderColor: C.accent }}>
                  <p className="text-xs font-bold" style={{ color: C.accent }}>Marketing Check</p>
                  <p className="text-xs text-gray-600">7 Analysen · {formatPrice(PAKET_PRICES.marketing)} · Vollständiges Marketing-Audit</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Website Check */}
            <div className="border-2 p-8" style={{ borderColor: C.teal }}>
              <div className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: C.teal }}>Website Realitäts-Check™</div>
              <div className="font-black text-3xl mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>197 € <span className="text-base font-normal text-gray-400">inkl. MwSt.</span></div>
              <p className="text-sm text-gray-600 mb-2">Für Unternehmen, die ihre Website technisch und rechtlich auf Vordermann bringen wollen.</p>
              <p className="text-xs text-gray-500 mb-4 italic">Ideal für: Lokale Dienstleister, KMUs, Handwerksbetriebe, Gastronomen</p>
              <div className="space-y-2 mb-6">
                {[
                  { icon: "/manus-storage/23_1ab7147e.svg", text: "SEO-Barometer™", note: "Suchmaschinen-Sichtbarkeit" },
                  { icon: "/manus-storage/1_55991aa3.svg", text: "Lokal-Barometer™", note: "Google Business + lokale Präsenz" },
                  { icon: "/manus-storage/16_9c3f7718.svg", text: "Accessibility-Barometer™", note: "WCAG 2.2 + European Accessibility Act" },
                  { icon: "/manus-storage/29_7f3bb394.svg", text: "Datenschutz-Barometer™", note: "DSGVO-Konformität" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <img src={item.icon} alt="" className="w-5 h-5 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                    <div>
                      <span className="font-medium text-gray-700">{item.text}</span>
                      <p className="text-xs text-gray-400">{item.note}</p>
                    </div>
                    <span className="ml-auto text-xs font-bold mt-0.5" style={{ color: C.teal }}>✓</span>
                  </div>
                ))}
                {[
                  { icon: "/manus-storage/11_094992a3.svg", text: "Sensorik-Barometer™" },
                  { icon: "/manus-storage/7_8bcad2d2.svg", text: "GEO-Barometer™" },
                  { icon: "/manus-storage/33_d51b7819.svg", text: "Conversion-Barometer™" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-300">
                    <img src={item.icon} alt="" className="w-5 h-5 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                    <span>{item.text}</span>
                    <span className="ml-auto text-xs text-gray-300">—</span>
                  </div>
                ))}
              </div>
              <CheckoutButton
                productId="website_realitaetscheck"
                label="Website Check wählen"
                accentColor={C.teal}
                returnPath="/website-realitaetscheck"
                fullWidth
              />
            </div>

            {/* Marketing Check */}
            <div className="border-2 p-8 relative" style={{ borderColor: C.accent, background: "#fafafa" }}>
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 text-xs font-bold text-white" style={{ backgroundColor: C.accent, fontFamily: "'DM Sans', sans-serif" }}>
                EMPFOHLEN
              </div>
              <div className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: C.accent }}>Pro Website-RealitätsCheck™</div>
              <div className="font-black text-3xl mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>397 € <span className="text-base font-normal text-gray-400">inkl. MwSt.</span></div>
              <p className="text-sm text-gray-600 mb-2">Für Unternehmen, die das vollständige Bild wollen – technisch, emotional, strategisch und rechtlich.</p>
              <p className="text-xs text-gray-500 mb-4 italic">Ideal für: Agenturen, E-Commerce, wachstumsorientierte KMUs, Coaches & Berater</p>
              <div className="space-y-2 mb-6">
                {ALL_BAROMETER.map((b, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <img src={b.icon} alt="" className="w-5 h-5 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                    <div>
                      <span className="font-medium text-gray-700">{b.name}</span>
                      <p className="text-xs text-gray-400">{b.desc.split(",")[0]}</p>
                    </div>
                    <span className="ml-auto text-xs font-bold mt-0.5" style={{ color: C.accent }}>✓</span>
                  </div>
                ))}
              </div>
              <CheckoutButton
                productId="marketing_realitaetscheck"
                label="Marketing Check wählen"
                accentColor={C.accent}
                returnPath="/marketing-realitaetscheck"
                fullWidth
              />
            </div>
          </div>
        </div>
      </section>

      {/* Beispiel-Ausschnitte alle 7 */}
      <section className="py-16 px-4 border-t border-gray-200" style={{ backgroundColor: "#f9f9f9" }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>Beispiel-Ergebnisse</div>
            <h2 className="font-black uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)" }}>
              So sehen alle 7 Analysen aus
            </h2>
            <p className="text-gray-500 text-sm mt-2 max-w-lg mx-auto">Echte Scores, Dimensionen und der wichtigste Optimierungspunkt – der vollständige Report enthält alle Details.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {PREVIEW_DATA.map(d => (
              <AnalysisPreviewCard key={d.title} data={d} />
            ))}
          </div>
        </div>
      </section>

      {/* Demo-Ausschnitt */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>Live-Vorschau</div>
            <h2 className="font-black uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)" }}>
              Alle 7 Analysen im Überblick
            </h2>
          </div>

          {/* Tab-Auswahl */}
          <div className="flex flex-wrap gap-2 justify-center mb-8">
            {ALL_BAROMETER.map((b, i) => (
              <button
                key={b.id}
                onClick={() => switchDemo(i)}
                className="px-3 py-2 text-xs font-bold tracking-widest uppercase transition-all"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  backgroundColor: activeDemo === i ? b.color : "transparent",
                  color: activeDemo === i ? "#fff" : C.muted,
                  border: `1px solid ${activeDemo === i ? b.color : C.border}`,
                }}
              >
                <><img src={b.icon} alt="" className="inline-block w-4 h-4 mr-1 object-contain align-middle" style={{ opacity: 0.85 }} />{b.name.replace("™", "").replace("-Barometer", "")}</>
              </button>
            ))}
          </div>

          {/* Demo-Fenster */}
          <div key={demoKey} className="bg-white border shadow-sm overflow-hidden" style={{ borderColor: C.border }}>
            <div className="flex items-center gap-2 px-4 py-2 border-b bg-gray-50" style={{ borderColor: C.border }}>
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
              </div>
              <div className="flex-1 bg-white border rounded text-xs px-3 py-1 text-gray-400" style={{ borderColor: C.border }}>
                barometer.werbeagentur-girenko.de{demo.path}
              </div>
            </div>
            <div className="p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-6 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center border-4 flex-shrink-0"
                    style={{ borderColor: demo.color, background: `${demo.color}12` }}>
                    <div>
                      <div className="font-black text-center leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.8rem", color: demo.color }}>
                        {demo.grade}
                      </div>
                      <div className="text-xs text-center text-gray-500">
                        <AnimatedScore target={demo.score} />%
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="font-black uppercase text-lg" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{demo.name}</div>
                    <div className="text-xs text-gray-500">Beispiel-Analyse · example.de</div>
                  </div>
                </div>
                <div className="flex-1 md:text-right">
                  <div className="inline-flex items-center gap-2 px-3 py-1 text-xs font-bold" style={{ backgroundColor: `${demo.color}15`, color: demo.color }}>
                    ✓ Analyse abgeschlossen
                  </div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-x-10">
                <div>
                  <div className="text-xs font-bold tracking-widest uppercase mb-4 text-gray-400">Dimensionen</div>
                  {demo.dimensions.map((d, i) => (
                    <DemoBar key={`${demoKey}-${i}`} label={d.label} score={d.score} max={d.max} color={demo.color} delay={i * 200} />
                  ))}
                </div>
                <div>
                  <div className="text-xs font-bold tracking-widest uppercase mb-4 text-gray-400">Top-Empfehlungen</div>
                  {["Sofortmaßnahme mit höchstem Impact", "Mittelfristige Optimierung", "Strategische Weiterentwicklung"].map((rec, i) => (
                    <div key={i} className="flex items-start gap-2 mb-3 p-3 border-l-2" style={{ borderColor: demo.color, backgroundColor: `${demo.color}08` }}>
                      <span className="text-xs font-bold mt-0.5" style={{ color: demo.color }}>→</span>
                      <span className="text-xs text-gray-700">{rec}</span>
                    </div>
                  ))}
                  <div className="mt-4 p-3 border text-center" style={{ borderColor: demo.color }}>
                    <div className="text-xs text-gray-500 mb-1">+ 20+ weitere Empfehlungen im Vollreport</div>
                    <div className="text-xs font-bold" style={{ color: demo.color }}>Inkl. PDF-Export</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Was macht diesen Check besonders */}
      <section className="py-16 px-4" style={{ backgroundColor: "#000" }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>Einzigartigkeit</div>
            <h2 className="font-black uppercase text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)" }}>
              Was kein anderes Tool bietet
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: "/manus-storage/11_094992a3.svg", title: "Sensorisches Marketing", desc: "Kein anderes Tool analysiert, wie deine Website auf das Unterbewusstsein wirkt – Farben, Typografie, Bildsprache, emotionale Trigger." },
              { icon: "/manus-storage/7_8bcad2d2.svg", title: "KI-Sichtbarkeit", desc: "Während andere noch Google optimieren, analysiere ich bereits, ob ChatGPT, Perplexity und Co. deine Website empfehlen würden." },
              { icon: "/manus-storage/32_04885248.svg", title: "Ganzheitlicher Score", desc: "Statt 7 separate Reports bekommst du ein einheitliches Bild: Wo steht dein Marketing wirklich? Was ist dringend, was strategisch?" },
            ].map((item, i) => (
              <div key={i} className="border border-gray-700 p-6">
                <img src={item.icon} alt="" className="w-9 h-9 mb-4 object-contain" style={{ opacity: 0.85 }} />
                <div className="font-black uppercase text-white mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.1rem" }}>{item.title}</div>
                <p className="text-sm text-gray-400 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <div className="text-xs text-gray-500 line-through mb-2">{SINGLE_TOTAL} € einzeln</div>
            <div className="font-black text-white mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(3rem, 6vw, 4.5rem)", lineHeight: 1 }}>
              {BUNDLE_PRICE} €
            </div>
            <div className="text-sm text-gray-400 mb-6">Alle 7 Analysen · Sofortergebnis · PDF-Export · Du sparst {SAVINGS} €</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              <div className="border border-gray-600 p-4 flex flex-col text-left">
                <p className="text-xs font-bold tracking-widest uppercase mb-1 text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Option 1</p>
                <h4 className="font-black uppercase text-base mb-1 text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Vollständige Analyse</h4>
                <p className="text-gray-400 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>7 Analysen, PDF-Report, Lieferung in 72h.</p>
                <div className="text-2xl font-black text-white mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{BUNDLE_PRICE} €</div>
                <CheckoutButton productId="marketing_realitaetscheck" label="Jetzt buchen" accentColor={C.accent} returnPath="/marketing-realitaetscheck" fullWidth />
              </div>
              <div className="border-2 p-4 flex flex-col text-left relative" style={{ borderColor: C.accent }}>
                <div className="absolute -top-3 left-3 px-2 py-0.5 text-white text-xs font-bold uppercase tracking-widest" style={{ backgroundColor: C.accent, fontFamily: "'DM Sans', sans-serif" }}>Empfohlen</div>
                <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>Option 2</p>
                <h4 className="font-black uppercase text-base mb-1 text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Analyse + VideoCall</h4>
                <p className="text-gray-400 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>7 Analysen + 1h persönliche Ergebnis-Interpretation mit Larissa per VideoCall.</p>
                <div className="text-2xl font-black mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.accent }}>{formatPrice(MARKETING_REALITAETSCHECK_MIT_VIDEOCALL)}</div>
                <p className="text-xs text-gray-500 mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>inkl. 1h-VideoCall (+{VIDEOCALL_1H_AUFPREIS} €)</p>
                <CheckoutButton productId="marketing_realitaetscheck_mit_videocall" label="Analyse + VideoCall" accentColor={C.accent} returnPath="/marketing-realitaetscheck" fullWidth />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Larissa-Sektion */}
      <section className="py-16 px-4" style={{ backgroundColor: C.bgGray }}>
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="relative overflow-hidden" style={{ maxHeight: "480px" }}>
                <img
                  src={PORTRAIT_IMG}
                  alt="Larissa Girenko – Expertin für Marketing-Analyse"
                  className="w-full object-cover object-top"
                  style={{ maxHeight: "480px" }}
                />
              </div>
            </div>
            <div>
              <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>Über die Entwicklerin</p>
              <h2 className="font-black uppercase leading-none mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 4vw, 3rem)" }}>
                Larissa Girenko
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Ich bin Gründerin der Werbeagentur Larissa Girenko und Entwicklerin der Marketing Barometer Suite. Der Pro Website-RealitätsCheck ist mein persönlichstes Produkt – er zeigt das vollständige Bild, das ich mir selbst immer gewünscht hätte, bevor ich Marketingentscheidungen treffe.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mb-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Kein Tool analysiert gleichzeitig, wie eine Website emotional wirkt, wie sie bei Google und KI rankt, ob sie DSGVO-konform ist, wie gut sie konvertiert und ob sie für alle zugänglich ist. Bis jetzt.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: "7", label: "Analysen in einem" },
                  { value: "397 €", label: "statt 509 €" },
                  { value: "500+", label: "Kriterien geprüft" },
                  { value: "PDF", label: "Export inklusive" },
                ].map((stat, i) => (
                  <div key={i} className="border-l-2 pl-3" style={{ borderColor: C.accent }}>
                    <div className="font-black text-xl" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.accent }}>{stat.value}</div>
                    <div className="text-xs text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t" style={{ borderColor: C.border }}>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="w-8 h-8 object-contain" />
            <span className="text-xs text-gray-500">© 2026 Werbeagentur Larissa Girenko</span>
          </div>
          <div className="flex gap-6 text-xs text-gray-400">
            <Link href="/impressum"><span className="hover:text-gray-600 cursor-pointer">Impressum</span></Link>
            <Link href="/datenschutz"><span className="hover:text-gray-600 cursor-pointer">Datenschutz</span></Link>
            <Link href="/website-realitaetscheck"><span className="cursor-pointer font-bold" style={{ color: C.teal }}>Website Realitäts-Check™ →</span></Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
